<template>
  <div class="marketplace">
    <div class="productMarketplace">
      <div class="product-lists">
        <!-- Skeleton loader for marketplace items while loading -->
        <div class="discreetLoader" v-if="isLoadingData">Loading...</div>

        <!-- Static prompt for viewers who do not belong to a unit -->
        <OrganisationCoinStatus
          v-if="
            !isItemAuthorityReadsFeatureEnabled &&
            !isLoadingData &&
            !currentUserOrgUID &&
            !buyerOrgHasCoinsState
          "
        >
          <!-- Custom content for 'no-transactions' slot -->
          <template v-slot:no-transactions>
            <p class="shortwidth">
              You need to belong to a unit to see the product prices of other
              units.
              <HelpHint
                :message="'To calculate the exchange rate, we need to know the total number of coins in two units. Since you do not belong to a unit we cannot calculate the exchange rate.'"
                icon="Why?"
              />
              <HelpHint
                :message="`When you create your coin with Unit you automatically create an organisation. Since coins and organisations come hand-in-hand, the term 'unit' can apply to both the coin and its founding organisation.`"
                icon="What's a unit?"
              />
            </p>
            <UnitButton @click="redirectToWallet">
              🤑 Create your unit
            </UnitButton>
          </template>
        </OrganisationCoinStatus>

        <!-- Display the product/service list or a message if empty -->
        <!-- <div
          v-if="
            !isLoadingData &&
            !filteredProducts.length &&
            !filteredServices.length
          "
        >
          👀 No items available
        </div> -->
        <div>
          <MasonryMarketplace
            ref="masonryMarketplaceRef"
            :products="filteredProducts || []"
            :services="filteredServices || []"
            :showItems="showItemsComputed"
            :organisationUid="orgUidForMasonry"
            :buyerOrgUID="currentUserOrgUID"
            :showSellingAction="showSellingAction"
            @favouritesUpdated="repopulateUserWishlist"
            @itemEdited="$emit('itemEdited')"
          >
            <template #post-action>
              <slot name="post-action"></slot>
            </template>
            <template #selling-action>
              <slot name="selling-action"></slot>
            </template>
            <template #above-grid>
              <!-- Favourites list (moved into MasonryMarketplace above-grid slot) -->
              <transition name="slide-fade">
                <div
                  v-if="showFavourites && !isItemAuthorityReadsFeatureEnabled"
                >
                  <p class="nomargintopsmallmarginbottom">Your favourites</p>
                  <ScrollableMarketplaceList
                    class="favourites"
                    :sortedProductsAndServices="userWishlistProducts"
                    :favourites="true"
                    @favouritesUpdated="repopulateUserWishlist"
                  />
                </div>
              </transition>
              <!-- Recently added list -->
              <transition name="slide-fade">
                <div
                  v-if="
                    showRecentlyAdded && !isItemAuthorityReadsFeatureEnabled
                  "
                >
                  <p class="nomargintopsmallmarginbottom">{{ title }}</p>
                  <ScrollableMarketplaceList
                    :sortedProducts="sortedByCreationDateProducts"
                    :sortedProductsAndServices="
                      sortedByCreationDateProductsAndServices
                    "
                    @favouritesUpdated="repopulateUserWishlist"
                    @noCoinsInBuyerOrg="handleNoCoinsInGrandparent"
                    @hasCoinsInBuyerOrg="handleHasCoinsInGrandparent"
                    @userHasNoOrganisations="handleUserHasNoOrganisations"
                    @userHasOrganisations="handleUserHasOrganisations"
                    @itemEdited="$emit('itemEdited')"
                  />
                </div>
              </transition>
            </template>
          </MasonryMarketplace>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watchEffect, watch } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { auth } from "@/firebase";
import ScrollableMarketplaceList from "@/components/DashboardComponents/EcosystemComponents/ProductListComponents/ScrollableMarketplaceList.vue";
import HelpHint from "@/components/GenericComponents/HelpHint.vue";
import OrganisationCoinStatus from "@/components/DashboardComponents/EcosystemComponents/OrganisationCoinStatus.vue";
import MasonryMarketplace from "@/components/DashboardComponents/EcosystemComponents/MasonryMarketplaceComponents/MasonryMarketplace.vue";
import UnitButton from "@/components/GenericComponents/UnitButton.vue";
import {
  isItemAuthorityReadsFeatureEnabled,
  isOrgContextFeatureEnabled,
  isWishlistFeatureEnabled,
} from "@/config/runtimeConfig";

export default {
  name: "MainMarketplace",
  inheritAttrs: false,
  components: {
    ScrollableMarketplaceList,
    HelpHint,
    OrganisationCoinStatus,
    MasonryMarketplace,
    UnitButton,
  },
  props: {
    products: {
      type: Array,
      default: () => [],
    },
    services: {
      type: Array,
      default: () => [],
    },
    title: String,
    buyerOrgHasCoins: Boolean,
    showRecentlyAdded: Boolean,
    organisationUid: String,
    // Forwarded by parent (Ecosystem) when using org-only mode
    showItems: {
      type: String,
      default: "allUsers",
    },
    // Forwarded toggle for selling action panel (passed through to Masonry -> FiltersComponent)
    showSellingAction: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit, expose }) {
    const store = useStore();
    const router = useRouter();

    const buyerOrgHasCoinsState = ref(props.buyerOrgHasCoins);
    const showFavourites = computed(
      () =>
        isWishlistFeatureEnabled &&
        store.getters["UIState/showWishlist"]
    );
    // This is needed for the economy restrictions UI
    const showOwnOrganisationOnly = computed(
      () => store.getters["UIState/showOwnOrganisationOnly"]
    );
    const isLoadingData = ref(true);
    // Derive robust showItems/orgUid for Masonry (fallback to UI state + local org UID)
    // Note: defined after currentUserOrgUID to avoid TDZ on first evaluation

    const redirectToWallet = () => {
      router.push({
        name: "wallet",
        query: currentUserOrgUID.value ? { org: currentUserOrgUID.value } : {},
      });
    };

    const userWishlistProducts = ref([]);
    const currentUserUid = computed(
      () =>
        store.getters.userCredentials?.data?.uid ||
        store.getters.user?.data?.uid ||
        auth.currentUser?.uid ||
        null
    );

    const currentUserOrgUID = computed(
      () =>
        props.organisationUid ||
        store.getters.selectedOrgUID ||
        store.getters.currentOrgUID ||
        null
    );
    const masonryMarketplaceRef = ref(null);
    const showItemsComputed = computed(() =>
      props.showItems === "organisation" || showOwnOrganisationOnly.value
        ? "organisation"
        : "allUsers"
    );
    const orgUidForMasonry = computed(
      () =>
        props.organisationUid ||
        currentUserOrgUID.value ||
        store.getters.currentOrgUID
    );
    const buyerHasOrganisations = ref(true);

    const repopulateUserWishlist = async () => {
      userWishlistProducts.value = [];
      emit("update:userHasWishlistItems", false);
    };

    const sortedByCreationDateProducts = computed(() => {
      return [...props.products]
        .sort((a, b) => {
          const timeA = a?.createdAt?.seconds
            ? a.createdAt.seconds * 1e9 + a.createdAt.nanoseconds
            : 0;
          const timeB = b?.createdAt?.seconds
            ? b.createdAt.seconds * 1e9 + b.createdAt.nanoseconds
            : 0;
          return timeB - timeA;
        })
        .slice(0, 10);
    });

    const sortedByCreationDateProductsAndServices = computed(() => {
      const allItems = [...props.products, ...props.services];
      return allItems
        .sort((a, b) => {
          const timeA = a?.createdAt?.seconds
            ? a.createdAt.seconds * 1e9 + a.createdAt.nanoseconds
            : 0;
          const timeB = b?.createdAt?.seconds
            ? b.createdAt.seconds * 1e9 + b.createdAt.nanoseconds
            : 0;
          return timeB - timeA;
        })
        .slice(0, 10);
    });

    const handleNoCoinsInGrandparent = () => {
      buyerOrgHasCoinsState.value = false;
      emit("noCoinsInBuyerOrg");
    };

    const handleUserHasNoOrganisations = () => {
      buyerHasOrganisations.value = false;
      emit("userHasNoOrganisations");
    };

    const handleUserHasOrganisations = () => {
      buyerHasOrganisations.value = true;
      emit("userHasOrganisations");
    };

    const handleHasCoinsInGrandparent = () => {
      buyerOrgHasCoinsState.value = true;
      emit("hasCoinsInBuyerOrg");
    };

    watchEffect(() => {
      const orgDetails = store.getters.getBuyerOrgDetails(
        currentUserOrgUID.value
      );
      // console.log("MainMarketplace - orgDetails:", orgDetails);
      // console.log(
      //   "MainMarketplace - buyerOrgHasCoins before:",
      //   buyerOrgHasCoins.value
      // );

      buyerOrgHasCoinsState.value =
        !!orgDetails && orgDetails.totalOrgCoins > 0;
      // console.log(
      //   "MainMarketplace - buyerOrgHasCoins after:",
      //   buyerOrgHasCoins.value
      // );
    });

    const filteredProducts = ref([]);
    const filteredServices = ref([]);
    // Pre-check organization trade permissions
    const fetchOrgTradePermissions = async () => {
      if (isItemAuthorityReadsFeatureEnabled) return;
      const uniqueOrgIds = new Set();

      // Collect unique org IDs from products and services
      props.products.forEach((product) => {
        if (product.organisation) uniqueOrgIds.add(product.organisation);
      });

      props.services.forEach((service) => {
        if (service.organisation) uniqueOrgIds.add(service.organisation);
      });

      // console.log(
      //   `[Economy] Checking trade permissions for ${uniqueOrgIds.size} organizations`
      // );

      for (const orgId of uniqueOrgIds) {
        if (orgId !== currentUserOrgUID.value) {
          await store.dispatch("UIState/checkOrgAllowsTrades", orgId);
        }
      }
    };

    // Cached result of allowed orgs (to avoid async filtering issues)
    const allowedTradeOrgs = ref(new Set());

    // Update the allowed orgs cache
    const updateAllowedOrgs = async () => {
      if (isItemAuthorityReadsFeatureEnabled) {
        allowedTradeOrgs.value = new Set();
        return;
      }
      // Always include current user's org
      const allowedOrgs = new Set();
      if (currentUserOrgUID.value) {
        allowedOrgs.add(currentUserOrgUID.value);
      }

      // Get unique org IDs
      const uniqueOrgIds = new Set();
      props.products.forEach((product) => {
        if (product.organisation) uniqueOrgIds.add(product.organisation);
      });

      props.services.forEach((service) => {
        if (service.organisation) uniqueOrgIds.add(service.organisation);
      });

      // Loop through each org ID
      for (const orgId of uniqueOrgIds) {
        if (orgId === currentUserOrgUID.value) {
          // Always include current user's org
          allowedOrgs.add(orgId);
        } else {
          // Get result from cached state (previously checked by fetchOrgTradePermissions)
          const orgAllowsTrades = await store.dispatch(
            "UIState/checkOrgAllowsTrades",
            orgId
          );
          if (orgAllowsTrades) {
            allowedOrgs.add(orgId);
          }
        }
      }

      // console.log(
      //   "[Economy] Updated allowed orgs for trading:",
      //   Array.from(allowedOrgs)
      // );
      allowedTradeOrgs.value = allowedOrgs;
    };

    const updateFilteredItems = () => {
      // Get the economy restrictions state
      const isTradeAllowed = store.getters["UIState/isTradeAllowed"];
      const showOwnOrg =
        props.organisationUid ||
        showOwnOrganisationOnly.value ||
        !isTradeAllowed;

      console.log("[Economy] Filtering marketplace items:", {
        isTradeAllowed,
        showOwnOrg,
        currentUserOrgUID: currentUserOrgUID.value,
      });

      // Apply filters
      if (showOwnOrg) {
        // If restricted or only viewing own organization, filter to this org only
        filteredProducts.value = props.products.filter((product) => {
          return (
            product.organisation ===
            (props.organisationUid || currentUserOrgUID.value)
          );
        });

        filteredServices.value = props.services.filter((service) => {
          return (
            service.organisation ===
            (props.organisationUid || currentUserOrgUID.value)
          );
        });
      } else {
        // Filter products and services to those from orgs that allow trades
        // Use the cached allowed orgs
        filteredProducts.value = props.products.filter((product) => {
          return allowedTradeOrgs.value.has(product.organisation);
        });

        filteredServices.value = props.services.filter((service) => {
          return allowedTradeOrgs.value.has(service.organisation);
        });
      }
    };

    // Watch for organizationUid changes
    watch(
      () => props.organisationUid,
      (newVal) => {
        if (newVal) {
          updateFilteredItems(); // Ensure this is called to update the filtered arrays
        } else {
          updateFilteredItems(); // Reset to show all products/services
        }
      },
      { immediate: true }
    );

    // Watch for product/service changes to update trade permissions only on
    // the legacy path. Private authority derives renderable Feed state on the
    // server and must not restart browser-side monetary enrichment.
    watch(
      () => [props.products.length, props.services.length],
      async () => {
        if (isOrgContextFeatureEnabled) return;
        await fetchOrgTradePermissions();
        await updateAllowedOrgs();
        updateFilteredItems();
      }
    );

    watchEffect(() => {
      const orgDetails = store.getters.getBuyerOrgDetails(
        currentUserOrgUID.value
      );
      buyerOrgHasCoinsState.value =
        !!orgDetails && orgDetails.totalOrgCoins > 0;
      if (!buyerOrgHasCoinsState.value || orgDetails.totalOrgCoins <= 0) {
        handleNoCoinsInGrandparent();
      } else if (buyerOrgHasCoinsState.value) {
        handleHasCoinsInGrandparent();
      }
      buyerHasOrganisations.value = (store.getters.userOrgIds || []).length > 0;
      if (!buyerHasOrganisations.value) {
        handleUserHasNoOrganisations();
      } else if (buyerHasOrganisations.value) {
        handleUserHasOrganisations();
      }
    });
    // Watch for changes in economy restrictions and products/services
    watchEffect(async () => {
      const isTradeAllowed = store.getters["UIState/isTradeAllowed"];
      const isRestrictionLoaded =
        store.getters["UIState/isEconomyRestrictionsLoaded"];

      // Only proceed if economy restrictions are loaded
      if (!isOrgContextFeatureEnabled && isRestrictionLoaded) {
        // If we have new products or the trade restrictions changed, update permissions
        await updateAllowedOrgs();
        // Then update the filtered items
        updateFilteredItems();
      }
    });

    const fetchEconomyRestrictions = async () => {
      if (isOrgContextFeatureEnabled) return;
      // Only fetch if we have the necessary data
      if (!currentUserUid.value || !currentUserOrgUID.value) {
        console.log(
          "[Economy] Cannot fetch economy restrictions - missing user or org ID"
        );
        return;
      }

      try {
        await store.dispatch("UIState/fetchEconomyRestrictions", {
          userUID: currentUserUid.value,
          orgUID: currentUserOrgUID.value,
        });

        // After getting restrictions, update filtered items
        await updateFilteredItems();
      } catch (error) {
        console.error("[Economy] Error fetching economy restrictions:", error);
      }
    };

    onMounted(async () => {
      if (isOrgContextFeatureEnabled || isItemAuthorityReadsFeatureEnabled) {
        // Masonry owns the complete bounded read. Wishlist, Wallet totals and
        // economy enrichment remain legacy and must not mount behind this gate.
        isLoadingData.value = false;
        return;
      }

      if (!currentUserUid.value) {
        isLoadingData.value = false;
        return;
      }

      // Timeline rendering is owned by MasonryMarketplace. These enrichments
      // must never hold its first paint.
      Promise.allSettled([
        repopulateUserWishlist(),
        fetchEconomyRestrictions(),
        fetchOrgTradePermissions(),
      ]).then(async () => {
        await updateAllowedOrgs();
        updateFilteredItems();
      });

      isLoadingData.value = false;
    });

    const refreshTimeline = (options = {}) =>
      masonryMarketplaceRef.value?.refreshTimeline?.(options);

    expose({ refreshTimeline });

    return {
      userWishlistProducts,
      sortedByCreationDateProducts,
      sortedByCreationDateProductsAndServices,
      repopulateUserWishlist,
      handleNoCoinsInGrandparent,
      handleHasCoinsInGrandparent,
      buyerOrgHasCoinsState,
      currentUserUid,
      currentUserOrgUID,
      masonryMarketplaceRef,
      isLoadingData,
      handleUserHasNoOrganisations,
      handleUserHasOrganisations,
      buyerHasOrganisations,
      redirectToWallet,
      showFavourites,
      updateFilteredItems,
      fetchOrgTradePermissions,
      updateAllowedOrgs,
      filteredProducts,
      filteredServices,
      // Forwarded to Masonry
      showItemsComputed,
      orgUidForMasonry,
      isItemAuthorityReadsFeatureEnabled,
    };
  },
};
</script>

<style scoped>
.discreetLoader {
  display: none;
  justify-content: center;
  align-items: center;
  height: 100%;
  font-size: var(--font-size-title);
  color: var(--color-border-subtle);
}

.product-lists {
  width: 100%;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}
</style>
